/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, message, Tag, Spin } from 'antd';
import servicePriceService, { PRICE_TYPE_LABELS } from '../../../services/servicePriceService';
import { insuranceCompanyService } from '../../../services/insuranceService';
import { formatCurrency as fmt } from '../../../lib/format';
import Spinner from '../../../components/ui/Spinner';

const EMPTY_FORM = {
  price_type: 'particular',
  insurance_company_id: '',
  price: '',
  cost_price: '',
  start_date: new Date().toISOString().split('T')[0],
  end_date: '',
};

const ServicePriceModal = ({ service, open, onClose }) => {
  const [prices, setPrices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const loadPrices = useCallback(async () => {
    if (!service) return;
    setLoading(true);
    try {
      const data = await servicePriceService.byService(service.id);
      setPrices(data);
    } catch {
      message.error('Error al cargar precios');
    } finally {
      setLoading(false);
    }
  }, [service]);

  const loadCompanies = useCallback(async () => {
    try {
      const res = await insuranceCompanyService.getAll({ per_page: 100 });
      setCompanies(res.data || []);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    if (open && service) {
      loadPrices();
      loadCompanies();
      setShowForm(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
    }
  }, [open, service, loadPrices, loadCompanies]);

  const handleSave = async () => {
    if (!form.price || isNaN(parseFloat(form.price))) {
      message.warning('Ingrese un precio válido');
      return;
    }
    if (form.price_type !== 'particular' && !form.insurance_company_id) {
      message.warning('Seleccione la aseguradora');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        service_id: service.id,
        price_type: form.price_type,
        insurance_company_id: form.price_type === 'particular' ? null : parseInt(form.insurance_company_id),
        price: parseFloat(form.price),
        cost_price: form.cost_price ? parseFloat(form.cost_price) : null,
        start_date: form.start_date || new Date().toISOString().split('T')[0],
        end_date: form.end_date || null,
        is_active: true,
      };

      if (editingId) {
        await servicePriceService.update(editingId, payload);
        message.success('Precio actualizado');
      } else {
        await servicePriceService.create(payload);
        message.success('Precio creado');
      }

      setShowForm(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      await loadPrices();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error al guardar precio';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (price) => {
    setForm({
      price_type: price.price_type,
      insurance_company_id: price.insurance_company_id ? String(price.insurance_company_id) : '',
      price: price.price,
      cost_price: price.cost_price ?? '',
      start_date: price.start_date,
      end_date: price.end_date ?? '',
    });
    setEditingId(price.id);
    setShowForm(true);
  };

  const handleDeactivate = async (price) => {
    try {
      await servicePriceService.deactivate(price.id);
      message.success('Precio desactivado');
      await loadPrices();
    } catch {
      message.error('Error al desactivar');
    }
  };

  const particular = prices.filter(p => p.price_type === 'particular');
  const ars = prices.filter(p => p.price_type !== 'particular');

  return (
    <Modal
      title={`Precios — ${service?.name ?? ''}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={680}
    >
      <Spin spinning={loading}>
        {/* Particular */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0 fw-bold">Tarifa Particular</h6>
            {particular.length === 0 && (
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => { setForm({ ...EMPTY_FORM, price_type: 'particular' }); setEditingId(null); setShowForm(true); }}
              >
                <i className="fa fa-plus me-1" /> Agregar
              </button>
            )}
          </div>
          {particular.length === 0 ? (
            <p className="text-muted small mb-0">Sin tarifa particular registrada.</p>
          ) : (
            <table className="table table-sm mb-0">
              <thead><tr>
                <th>Precio</th><th>Costo</th><th>Vigencia</th><th>Estado</th><th></th>
              </tr></thead>
              <tbody>
                {particular.map(p => (
                  <tr key={p.id}>
                    <td className="fw-bold">{fmt(p.price)}</td>
                    <td className="text-muted">{fmt(p.cost_price)}</td>
                    <td className="small">{p.start_date}{p.end_date ? ` → ${p.end_date}` : ''}</td>
                    <td><Tag color={p.is_active ? 'green' : 'default'}>{p.is_active ? 'Activo' : 'Inactivo'}</Tag></td>
                    <td>
                      <button className="btn btn-xs btn-link p-0 me-2" onClick={() => handleEdit(p)}>
                        <i className="fa fa-edit text-primary" />
                      </button>
                      {p.is_active && (
                        <button className="btn btn-xs btn-link p-0" onClick={() => handleDeactivate(p)}>
                          <i className="fa fa-ban text-secondary" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ARS */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0 fw-bold">Tarifas ARS / Seguros</h6>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => { setForm({ ...EMPTY_FORM, price_type: 'ars' }); setEditingId(null); setShowForm(true); }}
            >
              <i className="fa fa-plus me-1" /> Agregar
            </button>
          </div>
          {ars.length === 0 ? (
            <p className="text-muted small mb-0">Sin tarifas ARS registradas.</p>
          ) : (
            <table className="table table-sm mb-0">
              <thead><tr>
                <th>Aseguradora</th><th>Tipo</th><th>Precio</th><th>Estado</th><th></th>
              </tr></thead>
              <tbody>
                {ars.map(p => (
                  <tr key={p.id}>
                    <td>{p.insuranceCompany?.name ?? `ID ${p.insurance_company_id}`}</td>
                    <td><Tag>{PRICE_TYPE_LABELS[p.price_type] ?? p.price_type}</Tag></td>
                    <td className="fw-bold">{fmt(p.price)}</td>
                    <td><Tag color={p.is_active ? 'green' : 'default'}>{p.is_active ? 'Activo' : 'Inactivo'}</Tag></td>
                    <td>
                      <button className="btn btn-xs btn-link p-0 me-2" onClick={() => handleEdit(p)}>
                        <i className="fa fa-edit text-primary" />
                      </button>
                      {p.is_active && (
                        <button className="btn btn-xs btn-link p-0" onClick={() => handleDeactivate(p)}>
                          <i className="fa fa-ban text-secondary" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Form */}
        {showForm && (
          <div className="border rounded p-3 bg-light">
            <h6 className="mb-3">{editingId ? 'Editar precio' : 'Nuevo precio'}</h6>
            <div className="row g-2">
              <div className="col-md-6">
                <label className="form-label small">Tipo</label>
                <select
                  className="form-select form-select-sm"
                  value={form.price_type}
                  onChange={e => setForm(f => ({ ...f, price_type: e.target.value, insurance_company_id: '' }))}
                >
                  <option value="particular">Particular</option>
                  <option value="ars">ARS</option>
                  <option value="coop">Cooperativa</option>
                  <option value="mipyme">MIPYME</option>
                  <option value="other">Otro</option>
                </select>
              </div>
              {form.price_type !== 'particular' && (
                <div className="col-md-6">
                  <label className="form-label small">Aseguradora</label>
                  <select
                    className="form-select form-select-sm"
                    value={form.insurance_company_id}
                    onChange={e => setForm(f => ({ ...f, insurance_company_id: e.target.value }))}
                  >
                    <option value="">Seleccionar...</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="col-md-6">
                <label className="form-label small">Precio (RD$) <span className="text-danger">*</span></label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min={0}
                  step="0.01"
                  value={form.price}
                  onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small">Costo (RD$)</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min={0}
                  step="0.01"
                  value={form.cost_price}
                  onChange={e => setForm(f => ({ ...f, cost_price: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small">Fecha inicio</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={form.start_date}
                  onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small">Fecha fin</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={form.end_date}
                  onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="d-flex gap-2 mt-3">
              <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <Spinner size="sm" className="me-1" /> : <i className="fa fa-save me-1" />}
                {editingId ? 'Actualizar' : 'Guardar'}
              </button>
              <button className="btn btn-sm btn-secondary" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setEditingId(null); }}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default ServicePriceModal;
